/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { css, cva } from "@panda/css";

/**
 * Token mapping notes (Figma node 471:3119, easy.ac redesign):
 *   transparent/light/005       → eds.palette.transparent.light.005
 *   borderradius/24             → eds.borderRadius.24
 *   borderradius/8              → eds.borderRadius.8
 *   borderradius/16             → eds.borderRadius.16
 *   dimension/{8,12,16,20,24,32,72,128} → eds.dimension.{...}
 *   foreground/primary          → eds.text.primary
 *   foreground/secondary        → eds.text.secondary
 *
 * Hardcoded literals (no matching EDS token):
 *   36px (outer tier shell radius — EDS borderRadius scale stops at 32)
 *   170.667px / 10.667rem (feature image width — derived from 96px height × 16:9)
 *   697px / 43.5625rem (feature text block width — Figma `w-[697px]`)
 */

/**
 * Outer accordion item shell.
 * Figma: rounded-[36px], bg + border = transparent.light.005.
 */
export const tierItemStyles = cva({
	base: {
		width: "100%",
		backgroundColor: "eds.palette.transparent.light.005",
		border: "token(spacing.eds.dimension.1) solid token(colors.eds.palette.transparent.light.005)",
		borderRadius: "36px",
		outlineOffset: "eds.dimension.2",
		"&:has(:focus-visible)": { outline: "token(spacing.eds.dimension.2) solid #fff" },

		"& .tier-trigger": {
			cursor: "pointer",
			outline: "none",
			width: "100%",
			textAlign: "start",
			// Figma layers a second `transparent.light.005` bg on top of the outer tier so the
			// header reads slightly brighter than the content area below it. Two stacked 5%
			// whites = ~9.75% effective.
			backgroundColor: "eds.palette.transparent.light.005",
			// When OPEN the bottom of the header butts up against the content panel, so we
			// only round the top corners (matches Figma `rounded-tl-[36px] rounded-tr-[36px]`).
			// When CLOSED the trigger IS the entire visible card, so all four corners must
			// round to match the outer 36px shell — without this the header bg renders
			// square-bottom INSIDE the rounded outer.
			borderTopLeftRadius: "36px",
			borderTopRightRadius: "36px",
			// Bottom-radius animates in lockstep with `radixAccordionSlideDown/Up` (300ms,
			// easeInOutQuad). Without this transition Radix flips `data-state` instantly and
			// the bottom corners snap between 0 ↔ 36px while the panel is still sliding,
			// which reads as a visible pop.
			transition:
				"border-bottom-left-radius token(animations.eds.duration.300) token(animations.eds.easing.easeInOutQuad), border-bottom-right-radius token(animations.eds.duration.300) token(animations.eds.easing.easeInOutQuad)",
			"&[data-state='closed']": {
				borderBottomLeftRadius: "36px",
				borderBottomRightRadius: "36px",
			},
			"&:has(:focus-visible), &:is(:focus-visible,:hover)": {
				"& .tier-trigger-icon": {
					backgroundColor: "eds.fill.secondary.hover",
				},
			},
		},
		// Figma header row: pl-32 / pr-20 / py-20, h-72.
		"& .tier-trigger-row": {
			minHeight: "eds.dimension.72",
			paddingInlineStart: "eds.dimension.32",
			paddingInlineEnd: "eds.dimension.20",
			paddingBlock: "eds.dimension.20",
		},
		// Circular button-style chevron container — matches "Button Solid" treatment from Figma.
		// 200ms (instead of 100ms) gives the bg fade enough time to feel intentional rather
		// than snappy on hover-in / hover-out.
		"& .tier-trigger-icon": {
			transition: "background-color token(animations.eds.duration.200) token(animations.eds.easing.easeInOutQuad)",
			flexShrink: 0,
			height: "eds.dimension.32",
			width: "eds.dimension.32",
			padding: "eds.dimension.8",
			borderRadius: "eds.borderRadius.full",
			backgroundColor: "eds.fill.secondary.default",
		},
		// Chevron transitions: `transform` for the open/close rotation (300ms), plus
		// `color` + `fill` (200ms) so any inherited-color shift on hover fades smoothly
		// instead of snapping. EDS Icons render via `currentColor` so a parent color
		// change cascades into the SVG fill — without these properties in the transition
		// list the change reads as a hard step.
		"& .tier-trigger-chevron": {
			transition:
				"transform token(animations.eds.duration.300) token(animations.eds.easing.easeInOutQuad), color token(animations.eds.duration.200) token(animations.eds.easing.easeInOutQuad), fill token(animations.eds.duration.200) token(animations.eds.easing.easeInOutQuad)",
		},
		// Content panel animation — matches Radix open/close keyframes used elsewhere.
		// `contain: layout style paint` isolates the panel's reflow/repaint when the
		// height animates, so the browser doesn't re-layout / re-paint the rest of the
		// page on every frame. Without this, expanding a tier with several feature
		// cards (each with absolute-positioned images) chugs hard in real Chrome —
		// even though headless playwright reports clean 60fps because its compositor
		// behaves differently. `will-change: height` pre-allocates a layer for the
		// height animation itself.
		"& .tier-content": {
			overflow: "hidden",
			contain: "layout style paint",
			willChange: "height",
			"&[data-state='open']": {
				animation:
					"radixAccordionSlideDown token(animations.eds.duration.300) token(animations.eds.easing.easeInOutQuad)",
			},
			"&[data-state='closed']": {
				animation:
					"radixAccordionSlideUp token(animations.eds.duration.300) token(animations.eds.easing.easeInOutQuad)",
			},
		},
		// Figma content padding p-16, gap-16 between feature cards / CTA.
		"& .tier-content-inner": {
			padding: "eds.dimension.16",
			display: "flex",
			flexDirection: "column",
			gap: "eds.dimension.16",
		},
	},
	variants: {
		expanded: {
			true: {
				"& .tier-trigger-chevron": {
					transform: "rotate(180deg)",
				},
			},
		},
	},
});

/**
 * Feature card — three breakpoint states (Figma nodes 474:5735 / 472:4708 / 471:3119):
 *   - <sm (≤375)   column card, full-width image on top, natural height
 *   - sm  (768)+   row card, body STILL column inside (text+buttons stacked).
 *                  `min-height: 176` matches Figma's `h-[176px]` for cards with
 *                  short copy, but unconstrained `height` lets the card grow
 *                  taller when an author writes a longer description than the
 *                  spec assumes — content visible, layout intact.
 *                  Image 256 × 144 (aspect 16:9 against 144 inner height)
 *   - lg+ (1280+)  row card, body row inside (text left, buttons right).
 *                  `min-height: 128` (Figma `h-[128px]`); same auto-grow rules.
 *                  Image 170.67 × 96
 */
// IMPORTANT: every breakpoint-conditional property lives inside an `smDown` /
// `sm` / `lg` block — there is intentionally NO base-layer flexDirection /
// alignItems on the card (or width / aspect-ratio on the media element below).
//
// Why: Panda's dev-mode incremental CSS gen sometimes lands atomic responsive
// classes (`sm:flex-d_row`, `lg:w_auto`, etc.) inside the `utilities >
// compositions` sub-layer while equivalent BASE classes (`flex-d_column`,
// `w_100%`) end up in `utilities` proper. CSS layer cascade puts the parent
// layer above the named sub-layer, so the base rule silently wins over the
// breakpoint override at all viewport sizes — manifesting as "feature cards
// stuck in mobile column layout at desktop width" only after specific HMR /
// navigation transitions.
//
// Splitting the base case into `smDown` (a max-width media query) keeps every
// property variant in the SAME cascade slot (some media query, no bare class),
// eliminating the layer-priority race entirely.
export const featureCardStyles = css({
	width: "100%",
	backgroundColor: "eds.palette.transparent.light.005",
	border: "token(spacing.eds.dimension.1) solid token(colors.eds.palette.transparent.light.005)",
	borderRadius: "eds.borderRadius.24",
	padding: "eds.dimension.16",
	display: "flex",
	gap: "eds.dimension.16",
	// Isolate each card's paint so the panel-slide animation doesn't ripple paint
	// work across every sibling card.
	contain: "layout paint",
	smDown: {
		flexDirection: "column",
		alignItems: "stretch",
	},
	sm: {
		minHeight: "11rem", // 176px floor — grows for long copy
		flexDirection: "row",
		alignItems: "center",
	},
	lg: {
		minHeight: "eds.dimension.128", // 128px floor at 1280+
	},
});

/**
 * Image container — sized off the card's inner height (card height − 32 padding):
 *   - <sm (≤375)  full-width, aspect 16:9
 *   - sm  (768)+  256 × 144 (card 176 − 32 = 144 inner; 144 × 16/9 = 256)
 *   - lg+ (1280+) 170.67 × 96 (card 128 − 32 = 96 inner; 96 × 16/9 = 170.67)
 */
// Same layer-cascade hazard as featureCardStyles above — every responsive
// property lives in a media query (`smDown` / `sm` / `lg`), nothing in the base
// layer.
export const featureCardMediaStyles = css({
	position: "relative",
	overflow: "hidden",
	borderRadius: "eds.borderRadius.8",
	flexShrink: 0,
	smDown: {
		width: "100%",
		aspectRatio: "16 / 9",
	},
	sm: {
		width: "16rem", // 256px
		height: "9rem", // 144px
	},
	// Derive width from aspect-ratio at lg+ rather than hard-coding 10.667rem
	// (Panda treats decimal-arbitrary-value class names with embedded dots
	// erratically under HMR — using `aspectRatio: "16 / 9"` + `height: 6rem`
	// produces the same 170.67×96 box via a stable class name).
	lg: {
		width: "auto",
		height: "6rem", // 96px
		aspectRatio: "16 / 9",
	},
});

/**
 * Body block — text + buttons. Layout flips at lg (NOT md):
 *   - <lg  column body — text on top, buttons stacked below (matches both mobile
 *          column-card and md row-card-with-column-body)
 *   - lg+  row body — text fills available width up to 697px, buttons pinned right
 */
export const featureCardBodyStyles = css({
	display: "flex",
	flex: "1 0 0",
	gap: "eds.dimension.16",
	// flexDirection / alignItems split across `lgDown` + `lg` rather than base +
	// `lg` for the same layer-cascade reason described on featureCardStyles above.
	lgDown: {
		flexDirection: "column",
		alignItems: "flex-start",
	},
	lg: {
		alignItems: "center",
		flexDirection: "row",
		justifyContent: "space-between",
	},
	"& .feature-card-text": {
		display: "flex",
		flexDirection: "column",
		gap: "eds.dimension.8",
		minWidth: "0",
		// Below lg: text takes full width inside the column body.
		// At lg+ (1280): text grows to fill the body's free space, capped at 697px
		// (Figma `w-[697px]`). Using `flex: 1 1 0` + maxWidth instead of fixed width
		// so the text shrinks gracefully if the card is squeezed. `697px` (not
		// `43.5625rem`) — see media-styles comment about decimal-rem class
		// generation hazards under Panda + HMR.
		lg: {
			flex: "1 1 0",
			maxWidth: "697px",
		},
	},
	"& .feature-card-title": {
		textStyle: "headingSM",
		color: "eds.text.primary",
	},
	"& .feature-card-desc": {
		textStyle: "paragraphSM",
		color: "eds.text.secondary",
		lineHeight: "eds.lineHeight.165",
	},
});

/**
 * CTA strip (no fill, just border).
 * Figma: rounded-24, gap-24, p-16 desktop / py-32 mobile.
 */
// Same layer-cascade hazard as featureCardStyles — every flexDirection /
// textAlign / paddingBlock variant lives inside a media query (`mdDown` / `md`),
// nothing in the base layer.
export const ctaCardStyles = css({
	border: "token(spacing.eds.dimension.1) solid token(colors.eds.palette.transparent.light.005)",
	borderRadius: "eds.borderRadius.24",
	width: "100%",
	display: "flex",
	gap: "eds.dimension.24",
	alignItems: "center",
	paddingInline: "eds.dimension.16",
	mdDown: {
		flexDirection: "column",
		textAlign: "center",
		paddingBlock: "eds.dimension.32",
	},
	md: {
		flexDirection: "row",
		textAlign: "start",
		paddingBlock: "eds.dimension.16",
	},
	// 56×56 asset slot. Renders the asset twice — a blurred + scaled copy behind, then the
	// sharp foreground on top — to recreate the layered halo treatment from Figma
	// (originally a gradient-bg + colored-border container behind a smaller sharp container).
	"& .cta-asset-stack": {
		position: "relative",
		flexShrink: 0,
		width: "eds.dimension.56",
		height: "eds.dimension.56",
	},
	"& .cta-asset, & .cta-asset-glow": {
		position: "absolute",
		inset: 0,
		width: "100%",
		height: "100%",
		objectFit: "cover",
		borderRadius: "eds.borderRadius.16",
	},
	"& .cta-asset-glow": {
		// Glow halo. `filter: blur` is one of the more expensive composites — promote
		// to its own GPU layer with `will-change` so the animating panel above doesn't
		// need to re-rasterize the blurred image on every frame of the slide-down.
		filter: "blur(8px)",
		opacity: 0.6,
		transform: "scale(1.15)",
		pointerEvents: "none",
		willChange: "transform",
	},
	"& .cta-text": {
		display: "flex",
		flex: "1 0 0",
		flexDirection: "column",
		gap: "eds.dimension.8",
		// Split alignItems across `mdDown` / `md` so the alignment override at md
		// isn't competing with a base-layer rule across CSS layers.
		mdDown: { alignItems: "center" },
		md: { alignItems: "flex-start" },
	},
	"& .cta-title": {
		textStyle: "headingSM",
		color: "eds.text.primary",
	},
	"& .cta-desc": {
		textStyle: "paragraphSM",
		color: "eds.text.secondary",
		lineHeight: "eds.lineHeight.165",
	},
	"& .cta-button": {
		flexShrink: 0,
	},
});

/** Outer section layout — vertical stack of tiers, gap 16, max-width 1312px. */
export const tiersListStyles = css({
	display: "flex",
	flexDirection: "column",
	alignItems: "stretch",
	gap: "eds.dimension.16",
	width: "100%",
	maxWidth: "82rem",
	marginInline: "auto",
});
